/* prettier-ignore */

import { Component, computed, effect, inject, input, InputSignal, signal, ViewEncapsulation } from '@angular/core';
import { RequestService } from './service/request.service';
import { tap } from 'rxjs';
import { ChatStyles } from './interface/chatStyles.interface';
import { DataChatService } from './service/dataChat.service';
import { PoiSidebarComponent } from './components/poiSidebar/poiSidebar.component';
import { StatisticsService } from '../app/services/statistics.service';
import { WebSocketService } from './service/webSocket.service';
import { DebugService } from '../app/services/debug.service';
import { ToolsService } from '../app/services/tools.service';
import { ScrollService } from './service/scrollContainer.service';
import { ChatViewComponent } from './chatView/chatView.component';
import { CssStylesService } from './service/cssStyles.service';
import { v4 as uuidv4 } from 'uuid';
import { RestService } from 'src/app/services';
import { TranslateModule } from '@ngx-translate/core';
import { WebEntryTrackingService } from 'src/app/services/web-entry-tracking.service';

@Component({
  selector: 'app-chatbot',
  imports: [
    ChatViewComponent,
    TranslateModule,
    PoiSidebarComponent
  ],
  standalone: true,
  templateUrl: './chatbot.component.html',
  styleUrls: ['./chatbot.component.scss'],
  encapsulation: ViewEncapsulation.Emulated
})
export class NewChatBotComponent {
  private rest = inject(RestService);
  private statistics = inject(StatisticsService)
  private webSocketService = inject(WebSocketService);
  private debugService = inject(DebugService);
  private cssService = inject(CssStylesService);
  private webEntryTracking = inject(WebEntryTrackingService);

  public requestService = inject(RequestService);
  public toolsService = inject(ToolsService)
  public dataChat = inject(DataChatService);
  public scrollService = inject(ScrollService);

  private token?: string;

  //SE ESTABLECE EL CHAT A MOSTRAR
  public chatID: InputSignal<string> = input.required<string>();

  ngAfterViewInit() {
    this.startChatBot();
  }

  startChatBot() {
    if (!this.chatID())
      return;

    this.dataChat.id.set(this.chatID());
    this.token = this.statistics.sessionId;
    this.debugService.info(this.token, '[ChatBotComponent] TOKENCHAT');
    this.dataChat.token.set(this.token);

    // Asegura fila CMS web_visitor_geolocations con session_id = thread_ del chat
    // (Metrics une por esa clave). Puede reenviar si la entrada web fue antes del chat.
    this.webEntryTracking.rebindToChatThread();

    // HRS-3045 (bloque 4): los sockets solo se abren si voz/imagen los consumen;
    // si las capabilities llegan después (config/cookie), el efecto del servicio conecta.
    this.webSocketService.connectIfNeeded();
    if (this.dataChat.messages()?.length > 1) {
      // Re-init con historial: el config ya se cargó (marca ya aplicada) en el
      // arranque previo, así que el gate anti-flash no debe ocultar el chat aquí.
      this.dataChat.configLoaded.set(true);
      return;
    }

    this.getChatConfig();
  }

  getChatConfig() {
    this.requestService.getChatDataStyles().subscribe({
      next: (res) => {
        this.debugService.debug(res, '[ChatBotComponent] ChatBot Styles API Response');

        const data: ChatStyles = res.data;
        this.cssService.setCSSVariables(data);
        this.dataChat.configLoaded.set(true);

        const initialMessage = this.getInitialMessage(data);
        this.dataChat.initialMessage.set(initialMessage);

        const recommendedMessages = this.getRecommendedMessages(data);
        this.dataChat.recommendedMessages.set(recommendedMessages);

        this.getChatHistoryFromApi();
      },
      // Si el config falla, mostrar igualmente el chat (estilos por defecto) en
      // vez de dejarlo oculto: el gate es solo anti-flash, no debe degradar el site.
      error: () => this.dataChat.configLoaded.set(true),
    });
  };

  getChatHistoryFromApi(){
    this.requestService.getHistory().subscribe(res => {
      const data = JSON.parse(res?.conversations);
      if (data?.length === 0)
        return;

      this.dataChat.hasHistory.set(true);
      this.debugService.debug(data, '[CHAT HISTORY]');
      data?.entries?.forEach(e => {
        if (e.message_id)
          return;

        this.dataChat.messages.update((prev) => [
          ...prev,
          {
            id: e.completion_id ?? uuidv4(),
            role: e.is_chatbot ? 'assistant' : 'user',
            content: e.content,
            feedback: e.feedback,
            show: true,
          },
        ]);
      });
      this.scrollService.scrollToBottom();
    })
  }

  getRecommendedMessages(data: ChatStyles) {
    let recommendedMessages: any = data.new_recommended_messages;
    if (Object.values(recommendedMessages)?.some((msg: any) => msg.length))
      return recommendedMessages;

    const oldrecommendedMessages: any = data.recommended_messages ?? [];
    if (!oldrecommendedMessages?.length)
      return [];

    return this.rest.device()?.languages?.reduce((acc, lang) => {
      acc[lang] = oldrecommendedMessages;
      return acc;
    }, {});
  }

  getInitialMessage(data: ChatStyles) {
    let initialMessage = data.new_initial_messages;
    if (initialMessage)
      return initialMessage;

    const oldInitialMessage = data.initial_messages?.[0]
    if (!oldInitialMessage)
      return null;

    return this.rest.device()?.languages?.reduce((acc, lang) => {
      acc[lang] = oldInitialMessage;
      return acc;
    }, {});
  }

}